// Built-ins
import { useEffect, useState } from "react";

// Icons / Images
import { FiEdit } from "react-icons/fi";

// Modules / Functions
import axios from "axios";
import Swal from "sweetalert2";
import { Modal, Form } from "react-bootstrap";
import GuardedModal from "../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Vars
import { headers } from "../global_vars";
import SelectWithInlineCreate from "../common/SelectWithInlineCreate";
import AutocompleteSelect from "../common/AutocompleteSelect";
import AddCountryModal from "./create/add_country";
import DateInput from "../common/DateInput";


// API
const UPDATE_CASH = "https://ultima.icsgr.com/api/data_management/cash/";

// Country
export function EditCashCountryModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addCountryOpen, setAddCountryOpen] = useState(false);

  useEffect(() => {
    if (show) {
      setCountry(cash.country?.country_id || "");
      loadCountries();
    }
  }, [show, cash]);

  const loadCountries = async () => {
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.get("https://ultima.icsgr.com/api/regions/all_countries/", { headers: currentHeaders });
      setCountries(res?.data?.all_countries || []);
    } catch (e) {
      setCountries([]);
    }
  };

  const isValid = country.trim().length > 0;
  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", { country_id: country }, { headers: currentHeaders });
      Swal.fire("Success", "Country updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update country";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Country</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Country</Modal.Title></Modal.Header>
        <Modal.Body>
          <SelectWithInlineCreate
            label="Country *:"
            required
            value={country}
            onChange={setCountry}
            placeholderOption="Select country"
            onAddClick={() => setAddCountryOpen(true)}
            overviewLinkEntity="country"
          >
            {countries.map((c) => (
              <option key={c.country_id} value={c.country_id} title={c.country_id}>
                {c.title}
              </option>
            ))}
          </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Country is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          await loadCountries();
          const id = data?.country_id;
          if (id) setCountry(id);
          setAddCountryOpen(false);
        }}
      />
    </>
  );
}

// Transaction Date
export function EditCashTrandateModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [trandate, setTrandate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setTrandate(cash.trandate || ""); }, [show, cash]);

  const isValid = trandate && trandate.length > 0;
  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", { trandate }, { headers: currentHeaders });
      Swal.fire("Success", "Transaction date updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update date";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Transaction date</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Transaction date</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Transaction date *:</Form.Label>
            <DateInput value={trandate} onChange={(e) => setTrandate(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Date is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Consultant
export function EditCashConsultantModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [consultant, setConsultant] = useState("");
  const [consultants, setConsultants] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (show) {
      setConsultant(cash.consultant?.consultant_id || "");
      loadConsultants();
    }
  }, [show, cash]);

  const loadConsultants = async () => {
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.get("https://ultima.icsgr.com/api/administration/all_consultants/", { headers: currentHeaders });
      setConsultants(res?.data?.all_consultants || []);
    } catch (e) { setConsultants([]); }
  };

  const isValid = consultant.trim().length > 0;
  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", { consultant_id: consultant }, { headers: currentHeaders });
      Swal.fire("Success", "Consultant updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update consultant";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Consultant</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Consultant</Modal.Title></Modal.Header>
        <Modal.Body>
          <SelectWithInlineCreate
            label="Consultant *:"
            required
            value={consultant}
            onChange={setConsultant}
            placeholderOption="Select consultant"
            overviewLinkEntity="consultant"
          >
            {consultants.map((c) => (
              <option key={c.consultant_id} value={c.consultant_id} title={c.consultant_id}>
                {c.fullname}
              </option>
            ))}
          </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Consultant is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Kind
export function EditCashKindModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [kind, setKind] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setKind(cash.kind || ""); }, [show, cash]);
  const isValid = kind === 'E' || kind === 'P';

  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", { kind }, { headers: currentHeaders });
      Swal.fire("Success", "Kind updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update kind";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Kind</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Kind</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Kind *:</Form.Label>
            <AutocompleteSelect value={kind} onChange={setKind} placeholder="Select kind">
              <option value="E">Expense</option>
              <option value="P">Payment</option>
            </AutocompleteSelect>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Kind is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Currency
export function EditCashCurrencyModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [currency, setCurrency] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setCurrency(cash.currency || ""); }, [show, cash]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const payload = { currency: (currency || "").trim() || null };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", payload, { headers: currentHeaders });
      Swal.fire("Success", "Currency updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update currency";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Currency</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Currency</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Currency:</Form.Label>
            <Form.Control value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="e.g., EUR" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Optional</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Amount Expense (only when kind = E)
export function EditCashAmountExpenseModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setAmount(cash.amountexp ?? ""); }, [show, cash]);
  const handleSave = async () => {
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const payload = { amountexp: String(amount).trim() === '' ? null : amount };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", payload, { headers: currentHeaders });
      Swal.fire("Success", "Amount expense updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update amount expense";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  if (cash.kind !== 'E') return null;
  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Amount expense</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Amount expense</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Amount expense:</Form.Label>
            <Form.Control type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 100.00" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Amount Payment (only when kind = P)
export function EditCashAmountPaymentModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setAmount(cash.amountpay ?? ""); }, [show, cash]);
  const handleSave = async () => {
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const payload = { amountpay: String(amount).trim() === '' ? null : amount };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", payload, { headers: currentHeaders });
      Swal.fire("Success", "Amount payment updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update amount payment";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  if (cash.kind !== 'P') return null;
  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Amount payment</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Amount payment</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Amount payment:</Form.Label>
            <Form.Control type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 100.00" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Reason
export function EditCashReasonModal({ cash, update_state }) {
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setReason(cash.reason || ""); }, [show, cash]);
  const isValid = reason.trim().length >= 1 && reason.trim().length <= 120;
  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const res = await axios.put(UPDATE_CASH + cash.cash_id + "/", { reason: reason.trim() }, { headers: currentHeaders });
      Swal.fire("Success", "Reason updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update reason";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Reason</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Reason</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason *:</Form.Label>
            <Form.Control as="textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason (1-120 chars)" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Reason is required (1–120 chars)."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}


